// Reports related store state and actions.
import axiosApiClient from '../../api/axiosApiClient'
import _ from 'lodash'
import * as Sentry from '@sentry/vue'
import i18next from '@/utils/i18next'

export const reportTypes = ['EOD', 'DELIVERY_LOGS_V2', 'POD', 'RUN_SHEET']
export const runSheetLocations = ['PH_MNL']
export const runSheetLocales = ['en-PH']
export const runSheetClients = ['AVON_ECOMM', 'QATEST']

const initialFilter = {
	type: reportTypes[0],
	pickupDateFrom: '',
	pickupDateTo: '',
	locationId: '',
	clientId: '',
	page: 1,
	limit: 20
}

// State
export const state = {
	reportTypes,
	total: 0,
	lastPage: 0,
	result: [],
	filter: initialFilter,
	showErrorToast: false,
	toastTitle: '',
	toastMessage: ''
}

// Mutations
export const mutations = {
	SET_REPORTS(state, { result, lastPage, total }) {
		state.result = result
		state.lastPage = lastPage
		state.total = total
	},
	SET_FILTER(state, filter) {
		state.filter = { ...state.filter, ...filter }
	}
}

// Getters
export const getters = {}

// Actions
export const actions = {
	init({ dispatch }) {
		dispatch('resetFilter')
	},
	async generateReport({ commit, state, dispatch }, { filter, onFailed }) {
		try {
			const { data } = await axiosApiClient.post('/reports', filter)
			window.open(data.fileUrl)
		} catch (_) {
			onFailed()
		}
	},
	hideErrorToast() {
		state.showErrorToast = false
		state.toastTitle = ''
		state.toastMessage = ''
	},
	async generatePodReport({ commit, state }, { filter }) {
		try {
			const response = await axiosApiClient.post(
				'/portal/v1/reports',
				filter
			)
			state.showErrorToast = true
			state.toastTitle = 'Notice'
			state.toastMessage = i18next.t(
				`PORTAL.Reports.toast_message.generating`
			)
			return response
		} catch (err) {
			if (
				err.response.status === 422 &&
				(err.response.data.errors[0]?.detail?.includes(
					'No delivered deliveries added'
				) ||
					err.response.data.errors[0]?.detail?.includes(
						'Report generation is in progress'
					))
			) {
				state.showErrorToast = true
				state.toastTitle = 'Notice'
				state.toastMessage = i18next.t(
					'PORTAL.Reports.toast_message.in_progress'
				)
			} else {
				Sentry.captureException('POD:generatePodReport', {
					level: 'error',
					extra: {
						error: err
					}
				})
				state.showErrorToast = true
				state.toastTitle = 'Notice'
				state.toastMessage = i18next.t(
					'PORTAL.Reports.toast_message.error_generating'
				)
			}
		}
	},
	async generateRunSheetReport({ commit, state }, { filter }) {
		try {
			const response = await axiosApiClient.post(
				'/portal/v1/runsheets',
				filter
			)
			const data = _.get(response, 'data')
			if (data) {
				//	Get file name
				const contentDisposition = _.get(
					response,
					'headers.content-disposition'
				)
				const fileName = contentDisposition
					? contentDisposition.split('=')[1].replaceAll('"', '')
					: 'exportFile.csv'

				//	Download with file name
				const link = document.createElement('a')
				link.href =
					'data:text/csv;charset=utf-8,' + encodeURIComponent(data)
				link.target = '_blank'
				link.download = fileName
				link.click()
				URL.revokeObjectURL(link.href)
			}
		} catch (err) {
			if (
				err.response?.status === 422 &&
				_.get(err, 'response.data.errors.0.code') === 'VALIDATION_ERROR'
			) {
				alert(err.response.data.errors[0].detail)
			} else {
				Sentry.captureException('POD:generateRunSheetReport', {
					level: 'error',
					extra: {
						error: err
					}
				})
				alert(err)
			}
		}
	},
	async getReports({ commit, state, dispatch }, filter = {}) {
		try {
			const request = { ...state.filter, ...filter }
			const query =
				`type=${request.type}` +
				`&page=${request.page}` +
				`&pickupDateTo=${request.pickupDateTo}` +
				`&limit=${request.limit}` +
				`${
					request.locationId
						? '&locationId=' + request.locationId
						: ''
				}` +
				`${request.clientId ? '&clientId=' + request.clientId : ''}` +
				`${
					request.pickupDateFrom
						? '&pickupDateFrom=' + request.pickupDateFrom
						: ''
				}`
			const { data } = await axiosApiClient.get('/reports?' + query)

			commit('SET_REPORTS', data)
			commit('SET_FILTER', request)
		} catch (err) {
			if (
				err.response.status === 422 &&
				err.response.data.code === 'VALIDATION_ERROR'
			) {
				alert(err.response.data.err)
			}
		}
	},
	updateFilter({ commit }, filter = {}) {
		commit('SET_FILTER', filter)
	},
	resetFilter({ commit, rootGetters, rootState }) {
		if (rootGetters['auth/isAdmin']) {
			commit('SET_FILTER', initialFilter)
		} else {
			const clientInitialFilter = {
				...initialFilter,
				...(rootState.auth.currentClient
					? { clientId: rootState.auth.currentClient.id }
					: {})
			}
			commit('SET_FILTER', clientInitialFilter)
		}
	},
	updatePODReport({ commit }, reportData) {
		commit('SET_REPORTS', reportData)
	}
}
